/*
        SUMMARY
            0 - Imports
            1 - Const / Variables
            2 - Classes
            3 - Functions
*/

/*---------------------/ 0 - Imports /-------------------*/
require('./home-mobile/modules/contato-dropdown');
document.addEventListener('DOMContentLoaded', () => window.HomeContatoDropdown?.init());

/*---------------------/ 1 - Const - Variables /-------------------*/
const IS_MOBILE = window.innerWidth <= 768;
let torreSequenceManager = null;
let introVideoManager = null;
let empreendimentoInitialized = false;

/*---------------------/ 2 - Classes /-------------------*/

/**
 * Gerencia a sequência de frames da torre-ceu
 */
class TorreSequenceManager {
    constructor() {
        this.canvas = document.getElementById('torre-sequence-canvas');
        this.wrapper = document.querySelector('.torre-sequence-wrapper');
        this.spacer = document.querySelector('.torre-sequence-spacer');
        this.heroContainer = document.getElementById('container-predios-hero');
        this.textContent = this.heroContainer?.querySelector('.hero-content-predios');
        this.breadcrumb = this.heroContainer?.querySelector('.breadcrumb-nav-predios');
        this.backgroundWrapper = document.getElementById('background-wrapper-predios');
        this.crossfadeOverlay = document.getElementById('crossfade-overlay-predios');
        this.overlayTransitionDone = false;
        this.overlayFadeTimeout = null;
        this.overlayCleanupTimeout = null;

        this.isMobile = IS_MOBILE;

        if (!this.canvas || !this.wrapper || !this.spacer) {
            console.warn('TorreSequenceManager: Required elements not found');
            return;
        }

        this.context = this.canvas.getContext('2d', { alpha: false });
        this.totalFrames = this.isMobile ? 121 : 152;
        this.frameStep = 1;
        this.frameCount = this.totalFrames;
        this.currentFrame = 0;
        this.images = [];

        const frameDir = this.isMobile ? 'frame-torre-mobile' : 'frame-torre';
        this.framePath = window.APP_CONFIG?.assetPath
            ? `${window.APP_CONFIG.assetPath}site/img/empreendimento/${frameDir}`
            : `/site/img/empreendimento/${frameDir}`;
        this.canvasVisible = true;
        this.textsFadedOut = false;
        this.currentZoomScale = 1.0;
        this.currentOffsetX = 0;
        this.currentOffsetY = 0;

        this.setupCanvas();
        this.preloadImages();
    }

    setupCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;

        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 768; // resize needs live check
            this.canvas.width = window.innerWidth;
            this.canvas.height = window.innerHeight;
            this.render(this.currentZoomScale, this.currentOffsetX, this.currentOffsetY);
        });
    }

    currentFrameSrc(index) {
        const actualFrame = index * this.frameStep + 1;
        return `${this.framePath}/img_${actualFrame.toString().padStart(4, '0')}.avif`;
    }

    async preloadImages() {
        console.log('🎬 Torre: Starting frame preload...');

        const loadPromises = [];
        for (let i = 0; i < this.frameCount; i++) {
            const img = new Image();
            const src = this.currentFrameSrc(i);

            const promise = new Promise((resolve) => {
                img.onload = () => resolve(true);
                img.onerror = () => {
                    console.warn(`Failed to load frame ${i}: ${src}`);
                    resolve(false);
                };
            });

            img.src = src;
            this.images[i] = img;
            loadPromises.push(promise);
        }

        await Promise.all(loadPromises);
        console.log('✅ Torre: All frames preloaded');

        // Renderiza o primeiro frame
        this.render();

        // Configura animação de scroll
        this.setupScrollAnimation();
    }

    render(scale = 1.0, offsetX = 0, offsetY = 0) {
        const canvasWidth = this.canvas.width;
        const canvasHeight = this.canvas.height;

        this.context.clearRect(0, 0, canvasWidth, canvasHeight);

        const img = this.images[this.currentFrame];
        if (!img || !img.complete || !img.naturalWidth) return;

        const imageAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = canvasWidth / canvasHeight;

        let drawWidth, drawHeight, drawX, drawY;

        if (imageAspect > canvasAspect) {
            drawHeight = canvasHeight;
            drawWidth = drawHeight * imageAspect;
        } else {
            drawWidth = canvasWidth;
            drawHeight = drawWidth / imageAspect;
        }

        // Aplica zoom pelo centro do canvas
        if (scale !== 1.0) {
            drawWidth *= scale;
            drawHeight *= scale;
        }

        drawX = (canvasWidth - drawWidth) / 2 + offsetX;
        drawY = (canvasHeight - drawHeight) / 2 + offsetY;

        this.context.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    setupScrollAnimation() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            console.warn('GSAP ou ScrollTrigger não disponível');
            return;
        }

        // Milestones como proporção do total de frames
        const FRAME_TO_HIDE_TEXT = Math.round(this.frameCount * 0.066);
        const ZOOM_START_FRAME = Math.round(this.frameCount * 0.855);
        const ZOOM_END_FRAME = Math.round(this.frameCount * 0.954);
        const FADE_START_FRAME = Math.round(this.frameCount * 0.921);
        const FADE_END_FRAME = this.frameCount - 1;
        const MAX_ZOOM = 1.0;


        const updateFrame = () => {
            const spacerRect = this.spacer.getBoundingClientRect();
            const spacerTop = spacerRect.top + window.scrollY;
            const spacerHeight = spacerRect.height;
            const scrollY = window.scrollY;

            const progress = Math.max(0, Math.min(1, (scrollY - spacerTop) / spacerHeight));
            const targetFrame = Math.round(progress * (this.frameCount - 1));

            if (targetFrame !== this.currentFrame) {
                this.currentFrame = targetFrame;

                let zoomScale = 1.0;
                let offsetX = 0;
                let offsetY = 0;

                // Zoom progressivo nos frames finais (desktop e mobile)
                if (this.currentFrame >= ZOOM_START_FRAME) {
                    const zoomProgress = Math.min(1,
                        (this.currentFrame - ZOOM_START_FRAME) / (ZOOM_END_FRAME - ZOOM_START_FRAME)
                    );
                    const easedProgress = zoomProgress * zoomProgress * (3 - 2 * zoomProgress);
                    zoomScale = 1.0 + (MAX_ZOOM - 1.0) * easedProgress;
                }

                this.currentZoomScale = zoomScale;
                this.currentOffsetX = offsetX;
                this.currentOffsetY = offsetY;
                this.render(zoomScale, offsetX, offsetY);

                // Fade out dos textos
                if (this.currentFrame >= FRAME_TO_HIDE_TEXT && !this.textsFadedOut) {
                    this.fadeOutTexts();
                } else if (this.currentFrame < FRAME_TO_HIDE_TEXT && this.textsFadedOut) {
                    this.fadeInTexts();
                }

                // Instant swap canvas/background at last frame
                this.applyCrossFade(this.currentFrame >= this.frameCount - 1 ? 1 : 0);
            }
        };

        window.addEventListener('scroll', updateFrame);
        updateFrame();

        console.log('✅ Torre: Scroll animation setup complete');
    }

    fadeOutTexts() {
        if (!this.textContent || this.textsFadedOut) return;
        this.textsFadedOut = true;

        gsap.to(this.textContent, {
            opacity: 0,
            duration: 0.8,
            ease: 'power2.out'
        });

        if (this.breadcrumb) {
            gsap.to(this.breadcrumb, {
                opacity: 0,
                duration: 0.8,
                ease: 'power2.out'
            });
        }

        console.log('🎬 Torre: Textos fazendo fade out no frame', this.currentFrame);
    }

    fadeInTexts() {
        if (!this.textContent || !this.textsFadedOut) return;
        this.textsFadedOut = false;

        gsap.to(this.textContent, {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out'
        });

        if (this.breadcrumb) {
            gsap.to(this.breadcrumb, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out'
            });
        }

        console.log('🎬 Torre: Textos fazendo fade in no frame', this.currentFrame);
    }

    applyCrossFade(progress) {
        if (progress >= 1 && !this.overlayTransitionDone) {
            this.overlayTransitionDone = true;
            if (this.wrapper) this.wrapper.style.display = 'none';
            if (this.backgroundWrapper) {
                this.backgroundWrapper.style.visibility = 'visible';
                this.backgroundWrapper.style.opacity = '1';
            }
            if (this.crossfadeOverlay) {
                this.crossfadeOverlay.style.visibility = 'hidden';
                this.crossfadeOverlay.style.opacity = '0';
            }
            this.canvasVisible = false;
        } else if (progress < 1 && this.overlayTransitionDone) {
            this.overlayTransitionDone = false;
            if (this.wrapper) {
                this.wrapper.style.display = 'block';
                this.wrapper.style.opacity = '1';
            }
            if (this.backgroundWrapper) {
                this.backgroundWrapper.style.visibility = 'hidden';
                this.backgroundWrapper.style.opacity = '0';
            }
            this.canvasVisible = true;
        }
    }

    transitionOverlayToWrapper() {
        // Logic moved into applyCrossFade for instant swap
    }

    cancelOverlayTransition() {
        this.overlayTransitionDone = false;

        if (this.crossfadeOverlay) {
            this.crossfadeOverlay.style.visibility = 'hidden';
            this.crossfadeOverlay.style.opacity = '0';
        }

        if (this.backgroundWrapper) {
            this.backgroundWrapper.style.visibility = 'hidden';
            this.backgroundWrapper.style.opacity = '0';
        }
    }
}

/**
 * Desktop intro: scrubbed video + title line slide-up + brand fade-in,
 * mirroring the example flow. Hands off to the produtos crossfade at the end.
 */
class IntroVideoManager {
    constructor() {
        this.video = document.getElementById('introScrollVideo');
        this.wrapper = document.querySelector('.intro-video-wrapper');
        this.spacer = document.querySelector('.torre-sequence-spacer');
        this.heroContainer = document.getElementById('container-predios-hero');
        this.textGroup = this.heroContainer?.querySelector('.text-content-predios');
        this.titleEl = document.getElementById('heroTitlePredios');
        this.descEl = document.getElementById('heroDescPredios');
        this.brandEl = this.heroContainer?.querySelector('.logo-content-predios');
        this.breadcrumb = document.querySelector('.breadcrumb-nav-predios');
        this.backgroundWrapper = document.getElementById('background-wrapper-predios');
        this.crossfadeOverlay = document.getElementById('crossfade-overlay-predios');
        this.overlayTransitionDone = false;
        this.linesReady = false;
        this.lineEls = [];

        if (!this.video || !this.wrapper || !this.spacer) {
            return;
        }
    }

    init() {
        this.splitTitleIntoLines();
        this.setInitialState();

        // Load the video directly from the CDN. fetch()+blob is blocked by CORS on R2,
        // but the <video> element itself can still stream via byte-range without CORS.
        // preload="auto" + the video being visually pinned for ~200vh of scroll gives the
        // browser plenty of time to buffer the file before scrubbing starts.
        const src = this.video.getAttribute('data-src');
        if (src && !this.video.src) {
            this.video.src = src;
            this.video.load();
        }

        // Set up text/brand timeline + scroll listener immediately so the reveal works
        // even before the video metadata arrives. Video scrubbing kicks in once duration is known.
        this.setupScrollAnimation();

        const onMetadata = () => {
            try { this.video.currentTime = 0; } catch (e) {}
            this.video.pause();
        };

        if (this.video.readyState >= 1) {
            onMetadata();
        } else {
            this.video.addEventListener('loadedmetadata', onMetadata, { once: true });
        }
    }

    splitTitleIntoLines() {
        if (!this.titleEl) return;
        const original = this.titleEl.innerHTML.trim();
        // Split on <br> variants while preserving inline tags within each line.
        const rawLines = original.split(/<br\s*\/?>(?:\s*)/i).map(s => s.trim()).filter(Boolean);
        const segments = rawLines.length ? rawLines : [original];
        this.titleEl.innerHTML = segments
            .map(line => `<span class="line-wrapper"><span class="line">${line}</span></span>`)
            .join('');
        this.lineEls = Array.from(this.titleEl.querySelectorAll('.line'));
        this.linesReady = true;
    }

    setInitialState() {
        if (typeof gsap === 'undefined') return;
        if (this.lineEls.length) {
            gsap.set(this.lineEls, { yPercent: 110, autoAlpha: 0 });
        }
        if (this.descEl) gsap.set(this.descEl, { y: 20, autoAlpha: 0 });
        if (this.brandEl) gsap.set(this.brandEl, { x: 30, scale: 0.95, autoAlpha: 0 });
    }

    setupScrollAnimation() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            console.warn('GSAP/ScrollTrigger not available for IntroVideoManager');
            return;
        }
        gsap.registerPlugin(ScrollTrigger);

        const spacer = this.spacer;
        const triggerEl = document.getElementById('empreendimento-predios');

        // Drive video.currentTime directly from scroll — mirroring the canvas frame pattern.
        // GSAP can't tween non-style properties on a <video> reliably across browsers, so we
        // bypass the timeline for the video itself and animate it from a scroll listener.
        // IMPORTANT: Lenis hijacks the native scroll event, so when it's active we must
        // subscribe to lenis.on('scroll') instead of window's scroll event.
        const updateVideo = (scrollY) => {
            if (!this.video) return;
            const y = (typeof scrollY === 'number') ? scrollY : window.scrollY;
            const spacerRect = spacer.getBoundingClientRect();
            const spacerTop = spacerRect.top + window.scrollY;
            const spacerHeight = spacerRect.height || 1;
            const progress = Math.max(0, Math.min(1, (y - spacerTop) / spacerHeight));
            const duration = this.video.duration;
            if (duration && Number.isFinite(duration)) {
                try { this.video.currentTime = duration * Math.min(0.999, progress); } catch (e) {}
            }

            if (progress >= 0.99 && !this.overlayTransitionDone) {
                this.overlayTransitionDone = true;
                this.applyCrossFade(1);
            } else if (progress < 0.985 && this.overlayTransitionDone) {
                this.overlayTransitionDone = false;
                this.applyCrossFade(0);
            }
        };

        if (window.lenis && typeof window.lenis.on === 'function') {
            window.lenis.on('scroll', ({ scroll }) => updateVideo(scroll));
        } else {
            window.addEventListener('scroll', () => updateVideo(), { passive: true });
        }
        updateVideo();

        // Timeline only for the text/brand reveal & exit, scrubbed across the spacer scroll.
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: triggerEl,
                start: 'top top',
                end: () => `+=${spacer.offsetHeight}`,
                scrub: 0.5,
                invalidateOnRefresh: true
            }
        });

        if (this.lineEls.length) {
            tl.to(this.lineEls, {
                yPercent: 0,
                autoAlpha: 1,
                duration: 20,
                stagger: 4,
                ease: 'power3.out'
            }, 0);
        }
        if (this.descEl) {
            tl.to(this.descEl, {
                y: 0,
                autoAlpha: 1,
                duration: 15,
                ease: 'power2.out'
            }, 5);
        }
        if (this.brandEl) {
            tl.to(this.brandEl, {
                x: 0,
                scale: 1,
                autoAlpha: 1,
                duration: 20,
                ease: 'power3.out'
            }, 2);
        }

        // Exit — text + brand vanish upward as we approach the products handoff.
        if (this.lineEls.length) {
            tl.to(this.lineEls, {
                y: -30,
                autoAlpha: 0,
                duration: 20,
                stagger: 2,
                ease: 'power2.inOut'
            }, 55);
        }
        if (this.descEl) {
            tl.to(this.descEl, {
                y: -15,
                autoAlpha: 0,
                duration: 15,
                ease: 'power2.inOut'
            }, 60);
        }
        if (this.brandEl) {
            tl.to(this.brandEl, {
                scale: 0.95,
                autoAlpha: 0,
                duration: 20,
                ease: 'power2.inOut'
            }, 55);
        }
        if (this.breadcrumb) {
            tl.to(this.breadcrumb, {
                autoAlpha: 0,
                duration: 20,
                ease: 'power2.inOut'
            }, 55);
        }
    }

    applyCrossFade(progress) {
        if (progress >= 1) {
            if (this.wrapper) this.wrapper.style.display = 'none';
            if (this.backgroundWrapper) {
                this.backgroundWrapper.style.visibility = 'visible';
                this.backgroundWrapper.style.opacity = '1';
            }
            if (this.crossfadeOverlay) {
                this.crossfadeOverlay.style.visibility = 'hidden';
                this.crossfadeOverlay.style.opacity = '0';
            }
        } else {
            if (this.wrapper) {
                this.wrapper.style.display = 'block';
                this.wrapper.style.opacity = '1';
            }
            if (this.backgroundWrapper) {
                this.backgroundWrapper.style.visibility = 'hidden';
                this.backgroundWrapper.style.opacity = '0';
            }
        }
    }
}

/*---------------------/ 3 - Functions /-------------------*/

function safePlay(video, fallbackContainer) {
    if (!video) return Promise.resolve(false);

    // iOS is stricter with autoplay; enforce required inline/muted flags.
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const playPromise = video.play();
    if (!playPromise || typeof playPromise.then !== 'function') {
        if (fallbackContainer) fallbackContainer.classList.remove('video-fallback');
        return Promise.resolve(true);
    }

    return playPromise
        .then(() => {
            if (fallbackContainer) fallbackContainer.classList.remove('video-fallback');
            return true;
        })
        .catch((error) => {
            if (fallbackContainer) {
                if (video.poster) {
                    fallbackContainer.style.setProperty('--video-fallback-image', `url('${video.poster}')`);
                }
                fallbackContainer.classList.add('video-fallback');
            }
            console.warn('Video autoplay blocked, using fallback:', error);
            return false;
        });
}


// Toggle de play/pause do vídeo da seção Partnership
function initPartnershipVideo() {
    const partVideo = document.getElementById('partnershipVideo');
    const partBtn = document.getElementById('partnershipPlayBtn');
    const partMedia = document.getElementById('partnershipMedia');
    const partIcon = partBtn ? partBtn.querySelector('ion-icon') : null;

    if (!partVideo || !partBtn || !partMedia || !partIcon) return;

    const isIframe = partVideo.tagName === 'IFRAME' || partVideo.dataset.renderType === 'iframe';

    // Para YouTube/iframe: o player toca em loop+muted automaticamente.
    // O botão abre o lightbox com o vídeo completo (com som e controles).
    if (isIframe) {
        const href = partVideo.dataset.originalUrl || partVideo.src;

        partBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (typeof GLightbox === 'undefined' || !href) return;
            const lb = GLightbox({
                elements: [{ href, type: 'video', width: 900 }],
                autoplayVideos: true,
                skin: 'clean',
                touchNavigation: true,
                closeOnOutsideClick: true,
            });
            lb.open();
        });
        return;
    }

    const toggleVideo = () => {
        if (partVideo.paused) {
            const playPromise = partVideo.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.then(() => {
                    partVideo.muted = false;
                }).catch(() => {});
            } else {
                partVideo.muted = false;
            }
            partMedia.classList.add('is-playing');
            partIcon.setAttribute('name', 'pause');
        } else {
            partVideo.pause();
            partMedia.classList.remove('is-playing');
            partIcon.setAttribute('name', 'play');
        }
    };

    partBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleVideo();
    });

    partMedia.addEventListener('mouseenter', () => {
        if (!partVideo.paused) partIcon.setAttribute('name', 'pause');
    });

    partMedia.addEventListener('mouseleave', () => {
        if (!partVideo.paused) partIcon.setAttribute('name', 'play');
    });
}

// Reveal-up entrance animation for new sections
function initRevealAnimations() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
    }

    // Pills are positioned via CSS transform (translateY(-50%) etc.).
    // We must NOT animate `y` on them or we'll trample on their positioning.
    const POSITIONED_SELECTOR = '.fab-pill-section, .fab-pill-gallery';

    const sections = ['#empreendimento-partnership', '#empreendimento-bairro-features', '#empreendimento-bairro-galeria'];

    sections.forEach(sel => {
        const section = document.querySelector(sel);
        if (!section) return;

        const items = Array.from(section.querySelectorAll('.reveal-up'));
        if (!items.length) return;

        const positioned = items.filter(el => el.matches(POSITIONED_SELECTOR));
        const regular    = items.filter(el => !el.matches(POSITIONED_SELECTOR));

        const trigger = { trigger: section, start: 'top 85%', once: true };

        if (regular.length) {
            gsap.set(regular, { autoAlpha: 0, y: 50 });
            gsap.to(regular, {
                scrollTrigger: trigger,
                autoAlpha: 1,
                y: 0,
                duration: 1.1,
                stagger: 0.15,
                ease: 'power4.out'
            });
        }

        if (positioned.length) {
            // Opacity-only fade so the CSS positioning transform stays intact.
            gsap.set(positioned, { autoAlpha: 0 });
            gsap.to(positioned, {
                scrollTrigger: trigger,
                autoAlpha: 1,
                duration: 1.1,
                delay: 0.4,
                ease: 'power4.out'
            });
        }
    });

    // Ensure ScrollTrigger recalculates positions once everything is laid out.
    if (typeof ScrollTrigger !== 'undefined') {
        requestAnimationFrame(() => ScrollTrigger.refresh());
    }
}

// Slider/Hero da seção bairro-galeria
function initBairroGaleria() {
    const galSection = document.getElementById('empreendimento-bairro-galeria');
    if (!galSection) return;

    const slides = window.__gallerySlides || [];
    if (!slides.length) return;

    const galCard = document.getElementById('gal-content-card');
    const galHeadline = document.getElementById('gal-headline');
    const galBodyText = document.getElementById('gal-body-text');
    const galSlideLabel = document.getElementById('gal-slide-label');
    const galProgItems = galSection.querySelectorAll('.prog-item');
    const galSlideEls = galSection.querySelectorAll('.slide');

    if (!galSlideEls.length) return;

    let galCurrent = 0;
    let galAnimating = false;
    let galAutoTimer = null;
    let galProgTimeout = null;

    function startProgressBar() {
        const item = galProgItems[galCurrent];
        if (!item) return;

        clearTimeout(galProgTimeout);
        galProgItems.forEach((el, i) => {
            el.classList.toggle('active', i === galCurrent);
            const fill = el.querySelector('.prog-fill');
            if (!fill) return;
            if (typeof fill.getAnimations === 'function') {
                fill.getAnimations().forEach(a => a.cancel());
            }
            fill.style.width = '0%';
        });

        const fill = item.querySelector('.prog-fill');
        galProgTimeout = setTimeout(() => {
            if (fill && typeof fill.animate === 'function') {
                fill.animate(
                    [{ width: '0%' }, { width: '100%' }],
                    { duration: 6000, fill: 'forwards', easing: 'linear' }
                );
            }
        }, 650);
    }

    function goTo(next, direction) {
        direction = direction || 'forward';
        if (galAnimating || next === galCurrent || !galSlideEls[next]) return;

        galAnimating = true;
        const prev = galCurrent;
        galCurrent = next;
        const isForward = direction === 'forward';
        const prevSlide = galSlideEls[prev];
        const nextSlide = galSlideEls[next];

        if (galCard) {
            galCard.style.opacity = '0';
            galCard.style.transform = 'translateY(22px)';
        }

        prevSlide.classList.add('exiting', isForward ? 'forward' : 'backward');
        prevSlide.classList.remove('active');
        nextSlide.classList.add('entering', isForward ? 'forward' : 'backward');

        setTimeout(() => {
            prevSlide.classList.remove('exiting', 'forward', 'backward');
            nextSlide.classList.remove('entering', 'forward', 'backward');
            nextSlide.classList.add('active');

            const data = slides[galCurrent] || {};
            if (galHeadline) galHeadline.innerHTML = data.title || '';
            if (galBodyText) galBodyText.innerHTML = data.text || '';
            if (galSlideLabel) galSlideLabel.textContent = data.label || '';

            startProgressBar();

            setTimeout(() => {
                if (galCard) {
                    galCard.style.opacity = '1';
                    galCard.style.transform = 'translateY(0)';
                }
            }, 120);

            galAnimating = false;
        }, 1100);
    }

    function startAuto() {
        clearInterval(galAutoTimer);
        galAutoTimer = setInterval(() => {
            goTo((galCurrent + 1) % slides.length, 'forward');
        }, 6500);
    }

    const btnNext = document.getElementById('gal-btn-next');
    const btnPrev = document.getElementById('gal-btn-prev');

    if (btnNext) {
        btnNext.addEventListener('click', () => {
            goTo((galCurrent + 1) % slides.length, 'forward');
            startAuto();
        });
    }
    if (btnPrev) {
        btnPrev.addEventListener('click', () => {
            goTo((galCurrent - 1 + slides.length) % slides.length, 'backward');
            startAuto();
        });
    }

    galProgItems.forEach(item => {
        item.addEventListener('click', () => {
            const next = parseInt(item.dataset.idx, 10);
            if (Number.isNaN(next)) return;
            goTo(next, next > galCurrent ? 'forward' : 'backward');
            startAuto();
        });
    });

    if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.create({
            trigger: galSection,
            start: 'top 50%',
            onEnter: () => {
                galSection.classList.add('is-active');
                startProgressBar();
                startAuto();
            },
            onLeaveBack: () => {
                galSection.classList.remove('is-active');
                clearInterval(galAutoTimer);
                clearTimeout(galProgTimeout);
            }
        });
    } else {
        galSection.classList.add('is-active');
        startProgressBar();
        startAuto();
    }
}

// Inicializa a seção interativa de prédios
function initPrediosSection() {
    const prediosSection = document.getElementById('empreendimento-predios');
    if (!prediosSection) return;

    const wrapper = document.getElementById('background-wrapper-predios');
    if (!wrapper) return;

    // Desktop: pina a section "produtos" no topo por uma janela curta de scroll,
    // pra dar uma "travada leve" com ela toda na tela antes da próxima section.
    if (!IS_MOBILE && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.create({
            trigger: wrapper,
            start: 'top top',
            end: '+=75%',
            pin: true,
            pinSpacing: true,
            anticipatePin: 1,
        });
    }

    const prodTabs = wrapper.querySelectorAll('.produtos__tab');
    const prodBgs = wrapper.querySelectorAll('.produtos__bg');
    const prodPara = wrapper.querySelector('#prod-para');
    const prodDesc = wrapper.querySelector('#prod-desc');
    const prodLink = wrapper.querySelector('#prod-link');
    const mapAreas = wrapper.querySelectorAll('.map-area-predios');

    // Mobile: trocar fontes para imagens portrait
    if (IS_MOBILE) {
        prodBgs.forEach(bg => {
            const img = bg.querySelector('img');
            if (img && img.src) {
                img.src = img.src.replace('empreendimento-novas-imgs/', 'empreendimento-novas-imgs-mobile/');
            }
        });
    }

    // Preload das imagens dos backgrounds
    const imageSet = new Set();
    prodBgs.forEach(bg => {
        const img = bg.querySelector('img');
        if (img && img.src) imageSet.add(img.src);
    });
    const imagesToPreload = Array.from(imageSet);

    function preloadImages(urls) {
        return Promise.all(urls.map(url => new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        })));
    }

    preloadImages(imagesToPreload).then(() => {
        const loader = document.querySelector('.empreendimento-loader');
        if (loader) {
            if (typeof gsap !== 'undefined') {
                gsap.to(loader, {
                    opacity: 0,
                    duration: 0.8,
                    ease: 'power2.out',
                    onComplete: () => { loader.style.display = 'none'; }
                });
            } else {
                loader.style.opacity = '0';
                setTimeout(() => { loader.style.display = 'none'; }, 800);
            }
        }
        if (IS_MOBILE || !document.getElementById('torre-sequence-canvas')) {
            wrapper.style.visibility = 'visible';
            wrapper.style.opacity = '1';
        }
        console.log('✅ Prédios images preloaded successfully');
    });

    // Lê dados dos tabs
    const prodData = {};
    prodTabs.forEach(tab => {
        const key = tab.dataset.prod;
        prodData[key] = {
            para: tab.dataset.para || '',
            title: tab.dataset.title || '',
            desc: tab.dataset.desc || '',
            link: tab.dataset.href || '#'
        };
    });

    if (typeof gsap !== 'undefined' && prodPara && prodDesc && prodLink) {
        gsap.set([prodPara, prodDesc, prodLink], { autoAlpha: 0, y: 30 });
    }

    let pinnedProd = 'default';

    function updateProduto(prod) {
        prodTabs.forEach(t => t.classList.toggle('is-active', t.dataset.prod === prod && prod !== 'default'));
        prodBgs.forEach(bg => {
            bg.classList.remove('is-hover');
            bg.classList.toggle('is-active', bg.dataset.prod === prod);
        });

        const elements = [prodPara, prodDesc, prodLink].filter(Boolean);

        if (typeof gsap === 'undefined') {
            if (prodData[prod]) {
                if (prodPara) prodPara.innerHTML = prodData[prod].para;
                if (prodDesc) prodDesc.innerHTML = prodData[prod].desc;
                if (prodLink) {
                    prodLink.href = prodData[prod].link;
                    prodLink.classList.remove('produtos__cta--hidden');
                }
            } else {
                if (prodPara) prodPara.innerHTML = '';
                if (prodDesc) prodDesc.innerHTML = '';
                if (prodLink) prodLink.classList.add('produtos__cta--hidden');
            }
            return;
        }

        gsap.killTweensOf(elements);
        if (prodData[prod]) {
            gsap.to(elements, {
                autoAlpha: 0, y: -20, duration: 0.3, ease: 'power2.in',
                onComplete: () => {
                    if (prodPara) prodPara.innerHTML = prodData[prod].para;
                    if (prodDesc) prodDesc.innerHTML = prodData[prod].desc;
                    if (prodLink) prodLink.href = prodData[prod].link;
                    gsap.set(elements, { y: 30, autoAlpha: 0 });
                    if (prodLink) {
                        gsap.set(prodLink, { pointerEvents: 'auto' });
                        prodLink.classList.remove('produtos__cta--hidden');
                    }
                    gsap.to(elements, {
                        autoAlpha: 1, y: 0, duration: 0.8, ease: 'expo.out',
                        stagger: 0.1, clearProps: 'transform'
                    });
                }
            });
        } else {
            gsap.to(elements, {
                autoAlpha: 0, y: -20, duration: 0.4, ease: 'power2.in',
                onComplete: () => {
                    if (prodPara) prodPara.innerHTML = '';
                    if (prodDesc) prodDesc.innerHTML = '';
                    if (prodLink) prodLink.classList.add('produtos__cta--hidden');
                }
            });
        }
    }

    prodTabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const prod = tab.dataset.prod;
            pinnedProd = (prod === pinnedProd) ? 'default' : prod;
            updateProduto(pinnedProd);
        });
    });

    function setHoverPreview(prod) {
        prodBgs.forEach(bg => {
            const isPinned = bg.classList.contains('is-active');
            bg.classList.toggle('is-hover', !isPinned && bg.dataset.prod === prod);
        });
    }

    function clearHoverPreview() {
        prodBgs.forEach(bg => bg.classList.remove('is-hover'));
    }

    mapAreas.forEach(area => {
        area.addEventListener('mouseenter', () => setHoverPreview(area.dataset.id));
        area.addEventListener('mouseleave', clearHoverPreview);
        area.addEventListener('click', (e) => {
            e.preventDefault();
            const prod = area.dataset.id;
            pinnedProd = (prod === pinnedProd) ? 'default' : prod;
            updateProduto(pinnedProd);
            clearHoverPreview();
        });
    });

    console.log('✅ Prédios section initialized successfully');
}


// Inicializa backgrounds e videos das seções de espaços
function initEspacosBackgrounds() {
    const espacoItems = document.querySelectorAll('#empreendimento-espacos .espaco-item');
    if (!espacoItems.length) return;

    const isMobileEspacos = window.innerWidth <= 1024;

    function activateItem(item) {
        item.classList.add('active');
        const video = item.querySelector('.espaco-video');
        if (video) safePlay(video, item);
    }

    function deactivateItem(item) {
        item.classList.remove('active');
        const video = item.querySelector('.espaco-video');
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
    }

    espacoItems.forEach(item => {
        const bgUrl = item.getAttribute('data-bg');
        if (bgUrl) item.style.setProperty('--bg-image', `url('${bgUrl}')`);
    });

    if (isMobileEspacos) {
        let currentActiveItem = null;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (currentActiveItem && currentActiveItem !== entry.target) {
                        deactivateItem(currentActiveItem);
                    }
                    activateItem(entry.target);
                    currentActiveItem = entry.target;
                } else if (entry.target === currentActiveItem) {
                    deactivateItem(entry.target);
                    currentActiveItem = null;
                }
            });
        }, { threshold: 0.5, rootMargin: '-20% 0px -20% 0px' });

        espacoItems.forEach(item => observer.observe(item));
    } else {
        espacoItems.forEach(item => {
            const video = item.querySelector('.espaco-video');
            if (!video) return;
            item.addEventListener('mouseenter', () => safePlay(video, item));
            item.addEventListener('mouseleave', () => {
                video.pause();
                video.currentTime = 0;
            });
        });
    }
}

// Inicializa todos os componentes
function initAll() {
    if (empreendimentoInitialized) return;
    empreendimentoInitialized = true;

    // Desktop: vídeo scrubbed; Mobile: canvas frames
    if (IS_MOBILE) {
        if (document.getElementById('torre-sequence-canvas')) {
            torreSequenceManager = new TorreSequenceManager();
        }
    } else {
        if (document.getElementById('introScrollVideo')) {
            introVideoManager = new IntroVideoManager();
            introVideoManager.init();
        }
    }

    initPrediosSection();
    initPartnershipVideo();
    initBairroGaleria();
    initEspacosBackgrounds();
    initRevealAnimations();
}

// Inicializa quando o DOM estiver pronto
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}

// Reinicializa se a página for carregada via AJAX
window.addEventListener('load', () => {
    setTimeout(initAll, 500);
});
